// Requêtes du portail pour la participation et les événements (lot L5-C1, M17, M21) : espace du représentant
// (désignations en cours, quartier, expression d'un besoin), consultations publiques et réponse d'un usager
// connecté, calendrier public des événements. Réponses construites par l'API avec des schémas stricts.
import type { ConsultationPublique, ConsultationUsager, DesignationUsager, EvenementPublic, ExpressionBesoin, QuartierRepresentant } from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../../api/client';
import { useSessionUsager } from '../session/FournisseurSessionUsager';

type Collectivite = { code: string; nom: string };

export const CLES_PARTICIPATION = {
  quartiers: ['citoyen', 'quartiers'] as const,
  quartier: (id: string) => ['citoyen', 'quartier', id] as const,
  consultations: (code: string) => ['public', 'consultations', code] as const,
  consultation: (code: string, id: string, connecte: boolean) => ['citoyen', 'consultation', code, id, connecte] as const,
  evenements: (code: string) => ['public', 'evenements', code] as const,
};

const encoder = encodeURIComponent;

export function useMesQuartiers() {
  const connecte = useSessionUsager().etat === 'connecte';
  return useQuery({ queryKey: CLES_PARTICIPATION.quartiers, queryFn: ({ signal }) => requete<{ elements: DesignationUsager[] }>('/usager/quartiers', { signal }).then((r) => r.elements), enabled: connecte });
}

export function useQuartierRepresentant(id: string | undefined) {
  const connecte = useSessionUsager().etat === 'connecte';
  return useQuery({ queryKey: CLES_PARTICIPATION.quartier(id ?? ''), queryFn: ({ signal }) => requete<QuartierRepresentant>(`/usager/quartiers/${id}`, { signal }), enabled: connecte && Boolean(id), retry: false });
}

export const exprimerBesoin = (quartier: string, corps: ExpressionBesoin) => requete<{ id: string; numero: string }>(`/usager/quartiers/${quartier}/besoins`, { methode: 'POST', corps });

export function useConsultationsPubliques(code: string | null) {
  return useQuery({
    queryKey: CLES_PARTICIPATION.consultations(code ?? ''),
    queryFn: ({ signal }) => requete<{ collectivite: Collectivite; elements: ConsultationPublique[] }>(`/public/collectivites/${encoder(code as string)}/consultations`, { signal }),
    enabled: Boolean(code),
  });
}

/** Consultation : vue de l'usager connecté (son choix, réponse possible), sinon vue publique. */
export function useConsultation(code: string | null, id: string | undefined) {
  const connecte = useSessionUsager().etat === 'connecte';
  return useQuery({
    queryKey: CLES_PARTICIPATION.consultation(code ?? '', id ?? '', connecte),
    queryFn: ({ signal }) => connecte
      ? requete<ConsultationUsager>(`/usager/collectivites/${encoder(code as string)}/consultations/${id}`, { signal })
      : requete<{ consultation: ConsultationPublique }>(`/public/collectivites/${encoder(code as string)}/consultations/${id}`, { signal }).then((r) => r.consultation),
    enabled: Boolean(code && id),
    retry: false,
  });
}

export const repondre = (id: string, collectivite: string, choix: number) => requete<ConsultationUsager>(`/usager/consultations/${id}/reponse`, { methode: 'POST', corps: { collectivite, choix } });

export function useEvenementsPublics(code: string | null) {
  return useQuery({
    queryKey: CLES_PARTICIPATION.evenements(code ?? ''),
    queryFn: ({ signal }) => requete<{ collectivite: Collectivite; elements: EvenementPublic[] }>(`/public/collectivites/${encoder(code as string)}/evenements`, { signal }),
    enabled: Boolean(code),
    staleTime: 60_000,
  });
}
